import {
	mdiEmoticonHappyOutline,
	mdiFormatBold,
	mdiFormatItalic,
	mdiFormatListBulleted,
	mdiFormatListNumbered,
	mdiFormatUnderline,
	mdiLink,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Divider, IconButton, Popover } from '@mui/material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { LinkModal } from './components/LinkModal';
import { autoLinkifyQuillContent, containsUrls } from './utils/urlUtils';

// Basic QuillJS editor modules configuration
const quillModules = {
	toolbar: false, // Hide the default toolbar since we'll handle formatting with custom buttons
	clipboard: {
		matchVisual: false,
	},
};

// QuillJS editor formats
const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'indent', 'link'];

interface RichTextEditorProps {
	value: string;
	onChange: (content: string) => void;
	onKeyDown?: (e: React.KeyboardEvent) => void;
	placeholder?: string;
	disabled?: boolean;
	readOnly?: boolean;
	maxHeight?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
	value,
	onChange,
	onKeyDown,
	placeholder,
	disabled = false,
	readOnly = false,
	maxHeight = '72px',
}) => {
	const { t } = useTranslation();
	const quillRef = useRef<ReactQuill>(null);
	const [editorHeight, setEditorHeight] = useState<string>('24px');

	// Emoji picker state
	const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
	const emojiPickerOpen = Boolean(emojiAnchorEl);

	// Link modal state
	const [linkModalOpen, setLinkModalOpen] = useState(false);
	const [linkModalMode, setLinkModalMode] = useState<'create' | 'edit'>('create');
	const [linkModalInitialUrl, setLinkModalInitialUrl] = useState('');
	const [linkModalInitialText, setLinkModalInitialText] = useState('');
	const [savedLinkSelection, setSavedLinkSelection] = useState<{
		index: number;
		length: number;
	} | null>(null);

	// State to track the current editor selection for emoji insertion
	const [currentSelection, setCurrentSelection] = useState<{
		index: number;
		length: number;
	} | null>(null);

	// State to track current formatting
	const [currentFormat, setCurrentFormat] = useState<{
		bold?: boolean;
		italic?: boolean;
		underline?: boolean;
		list?: string | false;
		[key: string]: any;
	}>({});

	// Handle emoji button click
	const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		// Capture current selection before opening emoji picker
		const editor = quillRef.current?.getEditor();
		if (editor) {
			const range = editor.getSelection();
			if (range) {
				setCurrentSelection({
					index: range.index,
					length: range.length,
				});
			} else if (!currentSelection) {
				// If no current selection and nothing stored, default to end of content
				setCurrentSelection({
					index: editor.getLength(),
					length: 0,
				});
			}
		}

		// Open emoji picker
		setEmojiAnchorEl(event.currentTarget);
	};

	// Handle link formatting
	const handleLinkFormat = useCallback(() => {
		const editor = quillRef.current?.getEditor();
		if (editor) {
			const range = editor.getSelection();
			if (range) {
				const currentFormat = editor.getFormat(range);

				if (currentFormat.link) {
					// Remove link if already linked
					editor.format('link', false);
				} else if (range.length > 0) {
					// Get selected text for editing
					const selectedText = editor.getText(range.index, range.length);
					// Save the selection for later use
					const savedSelection = {
						index: range.index,
						length: range.length,
					};
					setSavedLinkSelection(savedSelection);

					// Open modal with selected text
					setLinkModalMode('create');
					setLinkModalInitialUrl(containsUrls(selectedText) ? selectedText : '');
					setLinkModalInitialText(selectedText);
					setLinkModalOpen(true);
				} else {
					// No selection, create new link at cursor position
					const savedSelection = {
						index: range.index,
						length: 0,
					};
					setSavedLinkSelection(savedSelection);

					setLinkModalMode('create');
					setLinkModalInitialUrl('');
					setLinkModalInitialText('');
					setLinkModalOpen(true);
				}
			}
		}
	}, []);

	// Handle link modal confirmation
	const handleLinkConfirm = useCallback(
		(url: string, text: string) => {
			const editor = quillRef.current?.getEditor();
			if (editor && savedLinkSelection) {
				// Use the saved selection instead of current selection
				const { index, length } = savedLinkSelection;

				if (length > 0) {
					// Replace selected text with link
					editor.deleteText(index, length);
					editor.insertText(index, text, 'link', url);
					// Move cursor after the inserted link
					editor.setSelection(index + text.length, 0);
				} else {
					// Insert new link at cursor position
					editor.insertText(index, text, 'link', url);
					// Move cursor after the inserted link
					editor.setSelection(index + text.length, 0);
				}

				// Clear saved selection
				setSavedLinkSelection(null);

				// Focus back to editor
				setTimeout(() => {
					editor.focus();
				}, 100);
			} else {
				console.log('Cannot apply link - missing editor or selection:', {
					hasEditor: !!editor,
					hasSavedSelection: !!savedLinkSelection,
				});
			}
		},
		[savedLinkSelection]
	);

	// Close emoji picker
	const handleEmojiClose = () => {
		setEmojiAnchorEl(null);
	};

	// Insert emoji into editor
	const onEmojiClick = (emojiData: EmojiClickData) => {
		const editor = quillRef.current?.getEditor();
		if (editor) {
			// Use stored selection or default to end of text
			const insertPosition = currentSelection ? currentSelection.index : editor.getLength();

			// Insert emoji at position
			editor.insertText(insertPosition, emojiData.emoji);

			// Calculate new cursor position after the emoji
			const newPosition = insertPosition + emojiData.emoji.length;

			// Update stored selection to position after emoji
			setCurrentSelection({
				index: newPosition,
				length: 0,
			});

			// Set active selection in the editor
			editor.setSelection(newPosition, 0);

			// Ensure editor has focus and cursor is properly positioned
			setTimeout(() => {
				const editorElement = editor.root;
				editorElement.focus();
				setTimeout(() => {
					editor.setSelection(newPosition, 0);
				}, 10);
			}, 0);
		}
		handleEmojiClose();
	};

	// Format handlers
	const handleFormat = useCallback((format: string, value?: any) => {
		const editor = quillRef.current?.getEditor();
		if (editor) {
			const range = editor.getSelection();
			if (range) {
				const currentFormat = editor.getFormat(range);

				if (format === 'list') {
					const isList = currentFormat.list === value;
					const newValue = isList ? false : value;
					editor.format('list', newValue);

					// Update format state immediately for visual feedback
					setCurrentFormat((prev) => ({
						...prev,
						list: newValue,
					}));
				} else {
					// For text formatting (bold, italic, underline), toggle on/off
					const isFormatActive = currentFormat[format] === true || currentFormat[format] === value;
					const newValue = isFormatActive ? false : value !== undefined ? value : true;

					if (range.length > 0) {
						// Format selected text
						editor.formatText(range.index, range.length, format, newValue);
						// Update format state immediately for visual feedback
						setCurrentFormat((prev) => ({
							...prev,
							[format]: newValue,
						}));
					} else {
						// Format at cursor position for future typing
						editor.format(format, newValue);
						// Update format state immediately for visual feedback
						setCurrentFormat((prev) => ({
							...prev,
							[format]: newValue,
						}));
					}
				}
			}
		}
	}, []);

	// Track selection changes and format state
	const handleSelectionChange = useCallback(() => {
		const editor = quillRef.current?.getEditor();
		if (editor) {
			const range = editor.getSelection();
			if (range) {
				setCurrentSelection({
					index: range.index,
					length: range.length,
				});

				// Update current format state for button styling
				const format = editor.getFormat(range);
				setCurrentFormat(format);
			}
		}
	}, []);

	// Reset formatting when content is cleared
	useEffect(() => {
		// When content is empty or just contains empty paragraph, reset formatting
		if (!value || value === '<p><br></p>' || value === '<p></p>' || value.trim() === '') {
			setCurrentFormat({});

			// Also clear formatting in the editor for future typing
			const editor = quillRef.current?.getEditor();
			if (editor) {
				const range = editor.getSelection();
				if (range) {
					editor.removeFormat(range.index, 0);
				}
			}
		}
	}, [value]);

	// Handle URL linkification with debouncing
	const linkifyContent = useCallback(() => {
		const editor = quillRef.current?.getEditor();
		if (!editor) return;

		const currentContent = editor.root.innerHTML;
		const hasUrls = containsUrls(currentContent);

		if (hasUrls) {
			const linkifiedContent = autoLinkifyQuillContent(currentContent);

			if (linkifiedContent !== currentContent) {
				// Get current selection to preserve cursor position
				const selection = editor.getSelection();

				// Update content while preserving selection
				const currentLength = editor.getLength();
				editor.root.innerHTML = linkifiedContent;

				// Restore cursor position at the end if no specific selection
				const newLength = editor.getLength();
				const cursorPosition = selection ? Math.min(selection.index, newLength - 1) : newLength - 1;

				setTimeout(() => {
					editor.setSelection(Math.max(0, cursorPosition), 0);
				}, 0);

				// Trigger onChange to keep parent component in sync
				onChange(linkifiedContent);
			}
		}
	}, [onChange]);

	// Initialize selection tracking when editor is ready
	useEffect(() => {
		const editor = quillRef.current?.getEditor();
		if (editor) {
			editor.on('selection-change', handleSelectionChange);

			return () => {
				editor.off('selection-change', handleSelectionChange);
			};
		}
	}, [handleSelectionChange]);

	// Auto-adjust height based on content
	useEffect(() => {
		// Skip if editor isn't ready yet
		if (!quillRef.current?.editor) return;

		const rafId = requestAnimationFrame(() => {
			const editorElement = quillRef.current?.editor?.root;
			if (editorElement) {
				const scrollHeight = editorElement.scrollHeight;
				const LINE_HEIGHT = 24;

				// Adjust height based on content (up to 4 lines)
				if (scrollHeight <= LINE_HEIGHT) {
					setEditorHeight(`${LINE_HEIGHT}px`);
				} else if (scrollHeight <= LINE_HEIGHT * 2) {
					setEditorHeight(`${LINE_HEIGHT * 2}px`);
				} else if (scrollHeight <= LINE_HEIGHT * 3) {
					setEditorHeight(`${LINE_HEIGHT * 3}px`);
				} else if (scrollHeight <= LINE_HEIGHT * 4) {
					setEditorHeight(`${LINE_HEIGHT * 4}px`);
				} else {
					setEditorHeight(`${LINE_HEIGHT * 4}px`);
				}

				// Add or remove scrollable class based on content height
				if (scrollHeight > LINE_HEIGHT * 4) {
					editorElement.classList.add('scrollable');
				} else {
					editorElement.classList.remove('scrollable');
				}
			}
		});

		return () => cancelAnimationFrame(rafId);
	}, [value]);

	// Auto-linkify URLs when content changes
	const handleContentChange = useCallback(
		(content: string, delta: any, source: any, editor: ReactQuill.UnprivilegedEditor) => {
			// Call original onChange first
			onChange(content);

			// Only auto-linkify on user input, not programmatic changes
			if (source === 'user') {
				// Check if this change was likely from a paste operation
				// Paste operations typically involve inserting a significant amount of text at once
				const isPasteOperation = delta.ops && delta.ops.some((op: any) => {
					// Consider it a paste if:
					// 1. It's an insert operation with more than 10 characters
					// 2. Or if it contains newlines (common in pasted content)
					if (op.insert && typeof op.insert === 'string') {
						const text = op.insert;
						return text.length > 10 || text.includes('\n');
					}
					return false;
				});

				if (isPasteOperation) {
					// Small delay to allow content to settle
					setTimeout(() => {
						linkifyContent();
					}, 200);
				}
			}
		},
		[onChange, linkifyContent]
	);

	// Add custom styles for Quill editor
	useEffect(() => {
		const style = document.createElement('style');
		style.id = 'quill-custom-styles';
		style.textContent = `
			.quill {
				border-radius: 24px;
				background-color: #f0f2f5;
				overflow: hidden;
				display: flex;
				flex-direction: column;
				width: 100%;
			}
			.ql-container {
				border: none !important;
				font-family: system-ui, -apple-system, sans-serif;
				font-size: 14px;
				background-color: transparent;
				width: 100%;
			}
			.ql-editor {
				min-height: 24px;
				max-height: ${maxHeight};
				overflow-y: hidden;
				padding: 10px 16px;
				line-height: 1.5;
				word-break: break-word;
				white-space: pre-wrap;
				width: 100%;
				overflow-wrap: break-word;
			}
			.ql-editor.scrollable {
				overflow-y: auto;
			}
			.ql-editor.ql-blank::before {
				color: #8e8e8e;
				font-style: normal;
				left: 16px;
			}
			.ql-editor p {
				margin: 0;
			}
			.ql-editor ul, .ql-editor ol {
				padding-left: 1.5em;
			}
		`;

		// Remove existing style if it exists
		const existingStyle = document.getElementById('quill-custom-styles');
		if (existingStyle) {
			existingStyle.remove();
		}

		document.head.append(style);

		return () => {
			style.remove();
		};
	}, [maxHeight]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				borderRadius: '24px',
				backgroundColor: '#f0f2f5',
				overflow: 'hidden',
				opacity: disabled ? 0.6 : 1,
				pointerEvents: disabled ? 'none' : 'auto',
			}}
		>
			{/* Formatting toolbar */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					padding: '6px 12px',
					borderBottom: '1px solid rgba(0,0,0,0.08)',
				}}
			>
				{/* Emoji */}
				<IconButton
					size="small"
					onClick={handleEmojiClick}
					disabled={disabled}
					sx={{
						p: 0.5,
						color: 'text.secondary',
						'&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
					}}
				>
					<Icon path={mdiEmoticonHappyOutline} size={0.7} />
				</IconButton>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

				{/* Text formatting */}
				<IconButton
					size="small"
					onClick={() => handleFormat('bold', true)}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.bold ? 'primary.main' : 'text.secondary',
						backgroundColor: currentFormat.bold ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor: currentFormat.bold ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiFormatBold} size={0.7} />
				</IconButton>

				<IconButton
					size="small"
					onClick={() => handleFormat('italic', true)}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.italic ? 'primary.main' : 'text.secondary',
						backgroundColor: currentFormat.italic ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor: currentFormat.italic
								? 'rgba(25, 118, 210, 0.2)'
								: 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiFormatItalic} size={0.7} />
				</IconButton>

				<IconButton
					size="small"
					onClick={() => handleFormat('underline', true)}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.underline ? 'primary.main' : 'text.secondary',
						backgroundColor: currentFormat.underline ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor: currentFormat.underline
								? 'rgba(25, 118, 210, 0.2)'
								: 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiFormatUnderline} size={0.7} />
				</IconButton>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

				{/* List formatting */}
				<IconButton
					size="small"
					onClick={() => handleFormat('list', 'bullet')}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.list === 'bullet' ? 'primary.main' : 'text.secondary',
						backgroundColor:
							currentFormat.list === 'bullet' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor:
								currentFormat.list === 'bullet' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiFormatListBulleted} size={0.7} />
				</IconButton>

				<IconButton
					size="small"
					onClick={() => handleFormat('list', 'ordered')}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.list === 'ordered' ? 'primary.main' : 'text.secondary',
						backgroundColor:
							currentFormat.list === 'ordered' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor:
								currentFormat.list === 'ordered' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiFormatListNumbered} size={0.7} />
				</IconButton>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

				{/* Link formatting */}
				<IconButton
					size="small"
					onClick={handleLinkFormat}
					disabled={disabled}
					sx={{
						p: 0.5,
						mr: 0.5,
						color: currentFormat.link ? 'primary.main' : 'text.secondary',
						backgroundColor: currentFormat.link ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
						'&:hover': {
							backgroundColor: currentFormat.link ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0,0,0,0.05)',
						},
					}}
				>
					<Icon path={mdiLink} size={0.7} />
				</IconButton>
			</Box>

			{/* Editor area */}
			<Box sx={{ flex: 1 }}>
				<ReactQuill
					ref={quillRef}
					theme="snow"
					value={value}
					onChange={handleContentChange}
					modules={quillModules}
					formats={quillFormats}
					placeholder={placeholder || t('chats.writeMessage')}
					onKeyDown={onKeyDown}
					readOnly={readOnly}
					style={{
						height: editorHeight,
						minHeight: '24px',
						maxHeight,
						lineHeight: '24px',
						transition: 'height 0.1s ease-out',
					}}
				/>
			</Box>

			{/* Emoji Picker Popover */}
			<Popover
				open={emojiPickerOpen && !disabled}
				anchorEl={emojiAnchorEl}
				onClose={handleEmojiClose}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<EmojiPicker onEmojiClick={onEmojiClick} />
			</Popover>

			{/* Link Modal */}
			<LinkModal
				open={linkModalOpen}
				onClose={() => {
					setLinkModalOpen(false);
					setSavedLinkSelection(null);
				}}
				onConfirm={handleLinkConfirm}
				initialUrl={linkModalInitialUrl}
				initialText={linkModalInitialText}
				mode={linkModalMode}
			/>
		</Box>
	);
};
